import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PipelineGraph } from "./PipelineGraph";
import { PipelineNodeInspector } from "./PipelineNodeInspector";
import { PipelineGroupInspector } from "./PipelineGroupInspector";
import { QueueExecutionHistory } from "./QueueExecutionHistory";
import { ExecutionChecks } from "./ExecutionChecks";
import { label } from "./format";
import type { MonitorStage, QueueRunDetail } from "./types";
import { useQueueResource } from "./useQueueResource";

export function QueueRunDialog({
  workload,
  onClose,
}: {
  workload: string;
  onClose: () => void;
}) {
  const [viewedEpoch, setViewedEpoch] = useState<number | null>(null);
  const runPath = `/pipeline/runs/${encodeURIComponent(workload)}`;
  const epochQuery = viewedEpoch === null ? "" : `?epoch=${viewedEpoch}`;
  const resource = useQueueResource<QueueRunDetail>(
    runPath + epochQuery,
    "detail",
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [extra, setExtra] = useState<MonitorStage[]>([]);
  const generation = resource.data?.projection_generation;
  const epoch = resource.data?.run.execution_epoch;
  useEffect(() => {
    setExtra([]);
    setCursor(null);
    setSelected(null);
    setSelectedGroup(null);
    setPinned(false);
  }, [generation, epoch]);
  const page = useQueueResource<QueueRunDetail>(
    cursor
      ? `${runPath}${epochQuery || "?"}${epochQuery ? "&" : ""}cursor=${encodeURIComponent(cursor)}`
      : null,
    "detail",
  );
  useEffect(() => {
    if (
      page.data &&
      page.data.projection_generation === generation &&
      page.data.run.execution_epoch === epoch
    )
      setExtra((previous) => [
        ...new Map(
          [...previous, ...page.data!.items].map((stage) => [
            stage.stage_instance_id,
            stage,
          ]),
        ).values(),
      ]);
  }, [page.data, generation, epoch]);
  const detail = useMemo(
    () =>
      resource.data
        ? {
            ...resource.data,
            items: resource.data.run.history_expired_at
              ? []
              : [
                  ...new Map(
                    [...extra, ...resource.data.items].map((stage) => [
                      stage.stage_instance_id,
                      stage,
                    ]),
                  ).values(),
                ],
          }
        : undefined,
    [resource.data, extra],
  );
  const inspect = useCallback(
    (stage: MonitorStage, pin: boolean) => {
      if (!pinned || pin) {
        setSelected(stage.stage_instance_id);
        setSelectedGroup(null);
        setPinned(pin);
      }
    },
    [pinned],
  );
  const previewGroup = useCallback(
    (group: string) => {
      if (!pinned) {
        setSelected(null);
        setSelectedGroup(group);
      }
    },
    [pinned],
  );
  const expand = useCallback(
    (group: string) => setExpanded((current) => new Set([...current, group])),
    [],
  );
  const stage = detail?.items.find(
    (item) => item.stage_instance_id === selected,
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document
            .querySelector<HTMLElement>(
              `[data-queue-workload="${CSS.escape(workload)}"]`,
            )
            ?.focus();
        }}
        className="max-h-[94vh] min-w-0 overflow-x-hidden overflow-y-auto [&>*]:min-w-0 sm:max-w-[min(96vw,1500px)]"
      >
        <DialogHeader>
          <DialogTitle className="break-all pr-8">
            Echo pipeline · {workload}
          </DialogTitle>
          <DialogDescription>
            Inspect observed stages, dependencies and attempts. Expand a group
            or use the text list to select a step.
          </DialogDescription>
        </DialogHeader>
        {resource.error && (
          <div role="status" className="rounded border p-3">
            {detail
              ? "Live updates unavailable. Showing previous evidence."
              : "This Echo is unavailable."}
            <Button variant="link" onClick={resource.reload}>
              Retry
            </Button>
          </div>
        )}
        {!detail && resource.loading && <p role="status">Loading pipeline…</p>}
        {detail && (
          <>
            <ExecutionChecks checks={detail.run.execution_checks} />
            {detail.run.history_expired_at && (
              <p role="status" className="rounded border p-3 text-sm">
                Detailed history has expired under the retention policy. The
                completion summary remains available.
              </p>
            )}
            <div className="flex flex-wrap gap-3 text-sm">
              <span>
                {detail.run.lifecycle === "active"
                  ? "In progress"
                  : `Left queue: ${label(detail.run.lifecycle)}`}
              </span>
              <span>
                {detail.run.product_flow} · {detail.run.channel}
              </span>
              <span className="text-muted-foreground">
                {detail.environment === "stg"
                  ? "Production (stg)"
                  : "Engineering staging (staging)"}
              </span>
            </div>
            <QueueExecutionHistory
              workload={workload}
              currentEpoch={
                detail.current_execution_epoch ?? detail.run.execution_epoch
              }
              viewedEpoch={detail.run.execution_epoch}
              onSelect={setViewedEpoch}
            />
            {detail.run.pipeline_definition_version !==
              detail.definition.version && (
              <p role="status">
                This pipeline version has partial graph support. Recorded steps
                remain available below.
              </p>
            )}
            {!detail.run.history_expired_at && (
              <div className="grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
                <div className="min-w-0 space-y-3">
                  <PipelineGraph
                    detail={detail}
                    expanded={expanded}
                    expand={expand}
                    inspect={inspect}
                    previewGroup={previewGroup}
                  />
                  <p className="text-xs text-muted-foreground">
                    Drag to move around the diagram. Use the zoom controls to
                    see more of the flow.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setExpanded(
                          new Set(
                            detail.definition.groups.map((group) => group.id),
                          ),
                        )
                      }
                    >
                      Expand groups
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpanded(new Set())}
                    >
                      Collapse groups
                    </Button>
                  </div>
                  <details>
                    <summary className="cursor-pointer font-medium">
                      Accessible step list ({detail.items.length} loaded)
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {detail.items.map((item) => (
                        <li key={item.stage_instance_id}>
                          <Button
                            variant="ghost"
                            className="h-auto w-full justify-start whitespace-normal text-left"
                            onClick={() => inspect(item, true)}
                          >
                            {detail.definition.stages.find(
                              (entry) => entry.id === item.stage_id,
                            )?.label ?? item.stage_id}{" "}
                            · {label(item.state)}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </details>
                  {(page.data?.next_cursor ??
                    (!cursor ? resource.data?.next_cursor : null)) && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        setCursor(
                          page.data?.next_cursor ??
                            resource.data?.next_cursor ??
                            null,
                        )
                      }
                    >
                      Load more steps
                    </Button>
                  )}
                  {page.error && (
                    <p role="status">
                      More steps are unavailable.{" "}
                      <Button variant="link" onClick={page.reload}>
                        Retry
                      </Button>
                    </p>
                  )}
                  <details className="text-xs text-muted-foreground">
                    <summary>Configuration and evidence</summary>
                    <p className="mt-2 break-all">
                      Frozen Echo release:{" "}
                      {detail.run.config_release_id ?? "Unrecorded"}
                    </p>
                    <p className="break-all">
                      Frozen digest: {detail.run.config_digest ?? "Unrecorded"}
                    </p>
                    <p>
                      Monitor release:{" "}
                      {detail.monitor_policy_ref.config_release_id}
                    </p>
                    <p>
                      Projection: {detail.projection_generation} · Revision{" "}
                      {detail.run.revision}
                    </p>
                  </details>
                </div>
                {stage ? (
                  <PipelineNodeInspector
                    key={stage.stage_instance_id}
                    stage={stage}
                    detail={detail}
                    pinned={pinned}
                    onUnpin={() => setPinned(false)}
                  />
                ) : selectedGroup ? (
                  <PipelineGroupInspector
                    group={selectedGroup}
                    detail={detail}
                    expand={expand}
                  />
                ) : (
                  <p className="rounded-lg border p-4 text-sm text-muted-foreground">
                    Hover or focus a step to preview its stats. Click to pin its
                    inputs, outputs and attempts.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
